import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { NowPlayingRef } from '@/types/lobby';

interface Props {
  nowPlaying: { ref: NowPlayingRef; source: string | null };
  onAccept: () => void;
  onDecline: () => void;
}

function describeRef(ref: NowPlayingRef, t: TFunction): string {
  if (ref.kind === 'movie') return t('lobby.join.refMovie');
  if (ref.kind === 'anime') return t('lobby.join.refAnime', { episode: ref.episode, audio: ref.audio });
  if (ref.kind === 'sports-vod') return t('lobby.join.refSportsVod');
  return t('lobby.join.refTv', { season: ref.season, episode: ref.episode });
}

/**
 * Modal prompt shown when the user is in a lobby with a current pick AND not
 * already on that pick's `/watch/` URL. Yes routes to the lobby content; No
 * dispatches `dismissJoinPrompt(ref)` so the prompt suppresses until the
 * lobby switches to a different ref.
 */
export function JoinPlaybackPrompt({ nowPlaying, onAccept, onDecline }: Props): JSX.Element {
  const { t } = useTranslation();
  const label = describeRef(nowPlaying.ref, t);
  return (
    <div className="join-prompt__backdrop" role="presentation">
      <div
        className="join-prompt"
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-prompt-title"
      >
        <h2 id="join-prompt-title" className="join-prompt__title">
          {t('lobby.join.title')}
        </h2>
        <p className="join-prompt__body">
          {t('lobby.join.body', { label })}
        </p>
        <div className="join-prompt__actions">
          <button
            type="button"
            className="btn"
            onClick={onAccept}
            data-testid="join-prompt-accept"
          >
            {t('lobby.join.watchNow')}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={onDecline}
            data-testid="join-prompt-decline"
          >
            {t('lobby.join.notNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
